import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Message, Radio } from 'semantic-ui-react';
import {
  Field,
  reduxForm,
  formValueSelector,
  SubmissionError,
  change,
} from 'redux-form';
import {
  showCreateHardwareModal,
  showEditHardwareModal,
  saveHardwareProduct,
} from 'state/hardwareCatalogue/actions';
import { requiredFieldValidation, minValueValidation } from 'utils/formUtils';

const validate = values => {
  const errors = {};

  const hardwareTypeError = requiredFieldValidation(
    values.name,
    'Hardware type'
  );
  if (hardwareTypeError) {
    errors.name = hardwareTypeError;
  }

  const priceRequiredError = requiredFieldValidation(
    values.price_per_unit,
    'Price'
  );
  if (priceRequiredError) {
    errors.price_per_unit = priceRequiredError;
  } else {
    const priceMinError = minValueValidation(
      Number(values.price_per_unit),
      0,
      'Price'
    );
    if (priceMinError) {
      errors.price_per_unit = priceMinError;
    }
  }

  const discountEligibleError = requiredFieldValidation(
    values.discount_eligible,
    'Discount eligibility'
  );
  if (discountEligibleError) {
    errors.discount_eligible = discountEligibleError;
  }

  if (values.discount_eligible === true) {
    const maxDiscountRequiredError = requiredFieldValidation(
      values.max_allowable_discount_percentage,
      'Max allowable discount percentage'
    );
    if (maxDiscountRequiredError) {
      errors.max_allowable_discount_percentage = maxDiscountRequiredError;
    } else {
      const maxDiscountMinError = minValueValidation(
        Number(values.max_allowable_discount_percentage),
        0,
        'Max allowable discount percentage'
      );
      if (maxDiscountMinError) {
        errors.max_allowable_discount_percentage = maxDiscountMinError;
      } else if (Number(values.max_allowable_discount_percentage) > 100) {
        errors.max_allowable_discount_percentage =
          'Max allowable discount percentage cannot exceed 100';
      }
    }
  }

  const stockStatusError = requiredFieldValidation(
    values.in_stock,
    'Stock status'
  );
  if (stockStatusError) {
    errors.in_stock = stockStatusError;
  }

  return errors;
};

const renderSelectField = ({
  input,
  meta,
  options,
  placeholder,
  onValueChange,
}) => {
  const hasError = meta.touched && meta.error;
  return (
    <Form.Field error={!!hasError}>
      <Form.Select
        options={options}
        placeholder={placeholder}
        value={input.value}
        onBlur={() => input.onBlur(input.value)}
        onChange={(e, { value }) => {
          input.onChange(value);
          if (onValueChange) {
            onValueChange(value);
          }
        }}
      />
      {hasError && (
        <div style={{ color: '#9f3a38', marginTop: '0.25rem' }}>
          {meta.error}
        </div>
      )}
    </Form.Field>
  );
};

renderSelectField.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  onValueChange: PropTypes.func,
};

renderSelectField.defaultProps = {
  placeholder: '',
  onValueChange: null,
};

const renderInputField = ({ input, meta, placeholder, disabled }) => {
  const hasError = meta.touched && meta.error;
  return (
    <Form.Field error={!!hasError}>
      <Form.Input
        {...input}
        placeholder={placeholder}
        disabled={disabled}
        value={input.value}
      />
      {hasError && (
        <div style={{ color: '#9f3a38', marginTop: '0.25rem' }}>
          {meta.error}
        </div>
      )}
    </Form.Field>
  );
};

renderInputField.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

renderInputField.defaultProps = {
  placeholder: '',
  disabled: false,
};

const renderPriceField = ({ input, meta, placeholder }) => {
  const hasError = meta.touched && meta.error;
  return (
    <Form.Field error={!!hasError}>
      <Form.Input
        {...input}
        type="number"
        min="0"
        step="1"
        placeholder={placeholder}
        value={input.value}
        onChange={e => input.onChange(e.target.value)}
      />
      {hasError && (
        <div style={{ color: '#9f3a38', marginTop: '0.25rem' }}>
          {meta.error}
        </div>
      )}
    </Form.Field>
  );
};

renderPriceField.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
};

renderPriceField.defaultProps = {
  placeholder: '',
};

export class RegisterHardwareModal extends React.Component {
  constructor(props) {
    super(props);
    this.onClose = this.onClose.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onHardwareTypeChanged = this.onHardwareTypeChanged.bind(this);
  }

  onClose() {
    const { dispatch, reset } = this.props;
    dispatch(showCreateHardwareModal(false));
    dispatch(showEditHardwareModal(false, null));
    reset();
  }

  onHardwareTypeChanged(baseProductName) {
    const { dispatch, baseProducts } = this.props;
    const selected = baseProducts.find(
      p => String(p.name) === String(baseProductName)
    );
    if (selected) {
      dispatch(change('hardwareEditor', 'name', selected.name));
      dispatch(change('hardwareEditor', 'sku', selected.sku));
    }
  }

  onSubmit(values) {
    const errors = validate(values);
    if (Object.keys(errors).length > 0) {
      throw new SubmissionError(errors);
    }

    const { dispatch, isEditMode, editingProduct } = this.props;

    const payload = {
      ...(isEditMode ? editingProduct : { type: 'HARDWARE', quantity: 1 }),
      name: values.name,
      sku: values.sku,
      price_per_unit: Number(values.price_per_unit),
      in_stock: values.in_stock,
      max_allowable_discount_percentage:
        values.discount_eligible === true
          ? Number(values.max_allowable_discount_percentage)
          : null,
    };

    const excludeKeys = ['tax', 'discount_rule', 'created_at', 'updated_at'];
    const cleanedPayload = Object.entries(payload)
      .filter(([key, value]) => value !== null && !excludeKeys.includes(key))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    return dispatch(saveHardwareProduct(cleanedPayload, isEditMode));
  }

  render() {
    const {
      isOpen,
      isEditMode,
      isLoading,
      errorMessage,
      handleSubmit,
      baseProducts,
      discountEligibleValue,
      stockStatusValue,
      dispatch,
    } = this.props;

    const baseProductOptions = baseProducts.map(p => ({
      value: p.name,
      text: p.name,
    }));

    const modalTitle = isEditMode ? 'Edit Hardware' : 'Register Hardware';

    return (
      <Modal open={isOpen} onClose={this.onClose} size="small">
        <Modal.Header>{modalTitle}</Modal.Header>
        <Modal.Content>
          {errorMessage && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{errorMessage}</p>
            </Message>
          )}
          <Form onSubmit={handleSubmit(this.onSubmit)} loading={isLoading}>
            <Form.Field required>
              <label>Hardware Type</label>
              <Field
                name="name"
                component={renderSelectField}
                options={baseProductOptions}
                placeholder="Select hardware type"
                onValueChange={this.onHardwareTypeChanged}
              />
            </Form.Field>

            <Form.Field required>
              <label>SKU</label>
              <Field
                name="sku"
                component={renderInputField}
                placeholder="Auto-generated from SAP"
                disabled
              />
            </Form.Field>

            <Form.Field required>
              <label>Price (IDR)</label>
              <Field
                name="price_per_unit"
                component={renderPriceField}
                placeholder="Enter price"
              />
            </Form.Field>

            <Form.Field required>
              <label>Discount Eligibility</label>
              <Form.Group>
                <Form.Field>
                  <Radio
                    label="No"
                    checked={discountEligibleValue === false}
                    onChange={() =>
                      dispatch(
                        change('hardwareEditor', 'discount_eligible', false)
                      )
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Radio
                    label="Yes"
                    checked={discountEligibleValue === true}
                    onChange={() =>
                      dispatch(
                        change('hardwareEditor', 'discount_eligible', true)
                      )
                    }
                  />
                </Form.Field>
              </Form.Group>
            </Form.Field>

            {discountEligibleValue === true && (
              <Form.Field required>
                <label>Max Allowable Discount Percentage (%)</label>
                <Field
                  name="max_allowable_discount_percentage"
                  component={renderPriceField}
                  placeholder="Enter percentage (0-100)"
                />
              </Form.Field>
            )}

            <Form.Field required>
              <label>Stock Status</label>
              <Form.Group>
                <Form.Field>
                  <Radio
                    label="In Stock"
                    checked={stockStatusValue === true}
                    onChange={() =>
                      dispatch(change('hardwareEditor', 'in_stock', true))
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Radio
                    label="Out of Stock"
                    checked={stockStatusValue === false}
                    onChange={() =>
                      dispatch(change('hardwareEditor', 'in_stock', false))
                    }
                  />
                </Form.Field>
              </Form.Group>
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.onClose}>Cancel</Button>
          <Button
            primary
            onClick={handleSubmit(this.onSubmit)}
            loading={isLoading}
          >
            {isEditMode ? 'Save' : 'Register'}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

RegisterHardwareModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  isEditMode: PropTypes.bool,
  editingProduct: PropTypes.object,
  isLoading: PropTypes.bool,
  errorMessage: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  baseProducts: PropTypes.array,
  discountEligibleValue: PropTypes.bool,
  stockStatusValue: PropTypes.bool,
};

RegisterHardwareModal.defaultProps = {
  isOpen: false,
  isEditMode: false,
  editingProduct: null,
  isLoading: false,
  errorMessage: null,
  baseProducts: [],
  discountEligibleValue: false,
  stockStatusValue: true,
};

const selector = formValueSelector('hardwareEditor');

const mapStateToProps = state => {
  const { editor, baseProducts } = state.hardwareCatalogue;
  const discountEligibleValue = selector(state, 'discount_eligible');
  const stockStatusValue = selector(state, 'in_stock');

  return {
    isOpen: editor.isOpen,
    isEditMode: editor.isEditMode,
    editingProduct: editor.editingProduct,
    isLoading: editor.isLoading,
    errorMessage: editor.errorMessage,
    baseProducts,
    initialValues: {
      name: editor.editingProduct && editor.editingProduct.name,
      sku: editor.editingProduct && editor.editingProduct.sku,
      price_per_unit:
        editor.editingProduct && editor.editingProduct.price_per_unit,
      discount_eligible:
        (editor.editingProduct &&
          editor.editingProduct.max_allowable_discount_percentage != null) ||
        false,
      max_allowable_discount_percentage:
        editor.editingProduct &&
        editor.editingProduct.max_allowable_discount_percentage,
      in_stock:
        editor.editingProduct && editor.editingProduct.in_stock != null
          ? editor.editingProduct.in_stock
          : true,
    },
    discountEligibleValue,
    stockStatusValue,
  };
};

const formWrapped = reduxForm({
  form: 'hardwareEditor',
  enableReinitialize: true,
  destroyOnUnmount: true,
})(RegisterHardwareModal);

export default connect(mapStateToProps)(formWrapped);
